import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

import cn from "classnames";

import { ImgixNextImage as Image } from "@components/imgixNextImage";

import { tagular } from "@cohesion/tagular";

import { ChevronRight } from "@icons/chevronRight";
import { LinkFill } from "@icons/link";

import { engine, getShareUrl, pivots } from "./engine";
import { CopyButton } from "./copy";

function SelectButton({ heading, category, image, onClick }) {
  return (
    <button
      type="button"
      className="flex items-center justify-between w-full"
      onClick={onClick}
    >
      <div className="flex items-center justify-start">
        <Image
          src={image}
          width={64}
          height={44}
          imgixParams={{ w: 64, h: 44, fit: "crop " }}
          className="rounded-pill"
        />

        <div className="ml-4 text-left">
          <h4
            className={cn("text-lg leading-tight", {
              capitalize: category !== "Campaign",
            })}
          >
            {heading}
          </h4>
          <p className="text-sm leading-none text-black-300">{category}</p>
        </div>
      </div>

      <ChevronRight />
    </button>
  );
}

SelectButton.propTypes = {
  heading: PropTypes.string.isRequired,
  category: PropTypes.string.isRequired,
  image: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
};

export function ShareButton({ heading, icon, onClick, caret = true }) {
  const Icon = icon;

  return (
    <button
      type="button"
      className="flex items-center justify-between w-full"
      onClick={onClick}
    >
      <div className="flex items-center justify-start">
        <div>
          <Icon />
        </div>
        <div className="ml-4 text-left">
          <h4 className="text-lg leading-tight capitalize">{heading}</h4>
        </div>
      </div>

      {caret && <ChevronRight />}
    </button>
  );
}

ShareButton.propTypes = {
  heading: PropTypes.string.isRequired,
  icon: PropTypes.elementType.isRequired,
  onClick: PropTypes.func.isRequired,
  caret: PropTypes.bool,
};

export function Share({ data = null }) {
  const [activePage, setActivePage] = useState(0);
  const [mode, setMode] = useState(null);

  const trackRef = useRef(null);

  const handleModeSelect = m => {
    setMode(m);
    setActivePage(1);
  };

  /**
   *
   * @param {enum} m mode - "campaign"|"category"|"destination"
   * @param {enum} p platform - "Facebook"|"Twitter"
   * @param {obj} d data - the destination data object
   */
  const handleShare = (m, p, d) => {
    const url = getShareUrl(m, p, d);

    tagular("click", {
      actionOutcome: "SHARE",
      outboundUrl: url,
      webElement: {
        location: "BITHOME",
        elementType: "BUTTON",
        position: "DESTINATION",
        text: m,
        name: p,
      },
    });

    window.open(
      url,
      "share",
      "toolbar=no,menubar=no,location=yes,resizable=no,scrollbars=yes,width=840,height=420,top=0,left=0"
    );
  };

  useEffect(() => {
    if (trackRef.current) {
      const { offsetWidth } = trackRef.current;

      trackRef.current.scrollTo({
        top: 0,
        left: offsetWidth * activePage,
        behavior: "smooth",
      });
    }
  }, [activePage]);

  const getShareHeader = () => {
    switch (mode) {
      case "destination":
        return `Share ${data.destination.name}`;
      case "category":
        return `Share ${pivots[data.category.name]}`;
      default:
        return "Share Best in Travel";
    }
  };

  if (!data) return null;

  return (
    <div className="relative max-w-2xl mx-auto">
      {/* HEADER SECTION */}
      <button
        type="button"
        className={cn("absolute top-0 left-0 p-4", {
          invisible: activePage === 0,
        })}
        onClick={() => {
          setActivePage(activePage - 1);
          setMode(null);
        }}
      >
        <ChevronRight className="rotate-180" />
      </button>

      <h3 className="mb-8 text-2xl text-center">
        {mode ? getShareHeader() : "Share"}
      </h3>

      <div
        className="flex overflow-x-hidden snap-x snap-mandatory"
        ref={trackRef}
      >
        {/* Page one - SHARE CATEGORY SELECTION */}
        <div className="flex-none w-full">
          <ul className="mx-auto divide-y lg:w-1/2 divide-black-200">
            <li className="py-4">
              <SelectButton
                heading={data.destination.name}
                image={data.destination.image}
                category="Destination"
                onClick={() => handleModeSelect("destination")}
              />
            </li>
            <li className="py-4">
              <SelectButton
                heading={data.category.name}
                image={data.category.image}
                category="Category"
                onClick={() => handleModeSelect("category")}
              />
            </li>
            <li className="py-4">
              <SelectButton
                heading={data.campaign.name}
                image={data.campaign.image}
                category="Campaign"
                onClick={() => handleModeSelect("campaign")}
              />
            </li>
          </ul>
        </div>

        {/* Page two - SHARE DETAIL */}
        <div className="flex items-center flex-none w-full gap-6 lg:gap-12">
          {mode && (
            <Image
              src={data[mode.toLowerCase()].image}
              width={230}
              height={300}
              imgixParams={{ w: 230, h: 300, fit: "crop" }}
              className="w-1/3 rounded-window"
            />
          )}

          <ul className="flex-grow divide-y divide-black-200">
            <li className="py-4">
              <CopyButton caret={false} icon={LinkFill} />
            </li>

            {engine.platforms.map(platform => (
              <li key={platform.name} className="py-4">
                <ShareButton
                  icon={platform.icon}
                  heading={platform.name}
                  onClick={() => handleShare(mode, platform.name, data)}
                />
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

Share.propTypes = {
  data: PropTypes.shape({
    campaign: PropTypes.shape({
      name: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
      image: PropTypes.string.isRequired,
    }).isRequired,
    category: PropTypes.shape({
      name: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
      image: PropTypes.string.isRequired,
    }).isRequired,
    destination: PropTypes.shape({
      name: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
      image: PropTypes.string.isRequired,
    }).isRequired,
  }),
};
